"use server";

import { db } from "@/lib/db";
import { User, Transaction } from "@prisma/client";

import { getSession } from "@/lib/session";
import { redirect } from "next/navigation";
import { cookies } from "next/headers";

export async function getDashboardData() {
    const { familyId } = await getSession();

    if (!familyId) {
        redirect("/onboarding");
    }

    const family = await db.family.findUnique({
        where: { id: familyId }
    });

    if (!family) {
        redirect("/onboarding");
    }

    const users = await db.user.findMany({
        // @ts-ignore - Pending Prisma schema sync for User.families
        where: { families: { some: { familyId: familyId } } },
    });

    const userIds = users.map((u: User) => u.id);

    const accounts = await db.account.findMany({
        where: { familyId: familyId },
    });

    const portfolioInvestments = await db.investment.findMany({
        where: { familyId: familyId },
        include: {
            // @ts-ignore - Pending Valuation relation sync
            valuations: {
                orderBy: { date: "desc" },
                take: 1
            },
            transactions: {
                select: { type: true, amount: true, date: true }
            }
        }
    });

    const portfolioValue = portfolioInvestments.reduce((sum: number, inv: any) => {
        const latestVal = inv.valuations[0];
        if (latestVal) return sum + Number(latestVal.currentValue);
        
        // Fallback to net invested
        const totalInvested = inv.transactions
            .filter((t: any) => ["buy", "sip", "top_up"].includes(t.type))
            .reduce((s: number, t: any) => s + Number(t.amount), 0);
        const totalWithdrawn = inv.transactions
            .filter((t: any) => ["sell", "maturity"].includes(t.type))
            .reduce((s: number, t: any) => s + Number(t.amount), 0);
        
        return sum + (totalInvested - totalWithdrawn);
    }, 0);

    // "Accounts" summary should include all accounts that aren't Credit Cards or Investment accounts
    const savingsAccounts = accounts.filter(a => a.type !== 'credit_card' && a.type !== 'investment');
    const investmentAccounts = accounts.filter(a => a.type === 'investment');
    const creditCardAccounts = accounts.filter(a => a.type === 'credit_card');

    const savings = savingsAccounts.reduce((sum, a) => sum + Number(a.balance), 0);
    const investments = investmentAccounts.reduce((sum, a) => sum + Number(a.balance), 0) + portfolioValue;
    const liabilities = creditCardAccounts.reduce((sum, a) => sum + Math.abs(Number(a.balance)), 0);
    
    // Net Balance = Savings + Investments - Liabilities
    const balance = savings + investments - liabilities;

    const transactions = await db.transaction.findMany({
        where: { recordedByUserId: { in: userIds } },
        orderBy: [
            { date: "asc" },
            { createdAt: "asc" }
        ],
        include: { category: true }
    });

    // --- 365-Day Daily Balances Chart Logic ---
    const chartDataMap: Record<string, { dateObj: Date; savings: number; liabilities: number; investments: number }> = {};
    const now = new Date();
    const oneYearAgo = new Date();
    oneYearAgo.setDate(now.getDate() - 364);
    oneYearAgo.setHours(0, 0, 0, 0);

    // 1. Group transactions by account and date for efficient lookup
    const txByAccount: Record<string, any[]> = {};
    transactions.forEach((t: any) => {
        if (!t.accountId) return;
        if (!txByAccount[t.accountId]) txByAccount[t.accountId] = [];
        txByAccount[t.accountId].push(t);
    });

    // 2. Reconstruct balances for each of the 365 days
    for (let i = 0; i < 365; i++) {
        const d = new Date(oneYearAgo);
        d.setDate(oneYearAgo.getDate() + i);
        const dayKey = d.toISOString().split("T")[0];
        
        let daySavings = 0;
        let dayLiabilities = 0;
        let dayInvestments = 0;

        // For each account, calculate its balance at the START of this day
        accounts.forEach(acc => {
            const initialDate = acc.initialDate ? new Date(acc.initialDate) : new Date(0);
            initialDate.setHours(0,0,0,0);

            // If the day is BEFORE the account even existed, it contributes 0
            if (d < initialDate) return;

            // Start with current balance and subtract all transactions that happened AFTER this day
            let reconstructedAccBalance = Number(acc.balance);
            const accountTxs = txByAccount[acc.id] || [];
            
            accountTxs.forEach(t => {
                const tDate = new Date(t.date);
                tDate.setHours(0,0,0,0);
                
                if (tDate > d) {
                    // Reverse the transaction
                    const amt = Number(t.amount);
                    if (acc.type === 'savings' || acc.type === 'checking' || acc.type === 'credit_card') {
                        reconstructedAccBalance -= (t.displayType === 'income' ? amt : -amt);
                    } else if (acc.type === 'investment') {
                        reconstructedAccBalance -= (t.displayType === 'income' ? amt : -amt);
                    }
                }
            });

            if (acc.type === 'savings' || acc.type === 'checking') {
                daySavings += reconstructedAccBalance;
            } else if (acc.type === 'credit_card') {
                dayLiabilities += Math.abs(reconstructedAccBalance);
            } else if (acc.type === 'investment') {
                dayInvestments += reconstructedAccBalance;
            }
        });

        // Add Portfolio Investment contributions (Gold/MFs held directly)
        portfolioInvestments.forEach(inv => {
            // Reconstruct valuation
            // Simpler: Use the earliest transaction date as "creation"
            const invTxs = inv.transactions || [];
            if (invTxs.length === 0) return;
            
            const firstTxDate = new Date(Math.min(...invTxs.map((t:any) => new Date(t.date).getTime())));
            firstTxDate.setHours(0,0,0,0);
            if (d < firstTxDate) return;

            const latestVal = inv.valuations?.[0];
            let invValue = latestVal ? Number(latestVal.currentValue) : 0;
            
            // If no valuation, calculate from transactions
            if (!latestVal) {
                const totalInvested = invTxs
                    .filter((t: any) => ["buy", "sip", "top_up"].includes(t.type))
                    .reduce((s: number, t: any) => s + Number(t.amount), 0);
                const totalWithdrawn = invTxs
                    .filter((t: any) => ["sell", "maturity"].includes(t.type))
                    .reduce((s: number, t: any) => s + Number(t.amount), 0);
                invValue = totalInvested - totalWithdrawn;
            }

            // Reverse transactions that happened AFTER this day
            invTxs.forEach((t: any) => {
                const tDate = new Date(t.date);
                tDate.setHours(0,0,0,0);
                if (tDate > d) {
                    const amt = Number(t.amount);
                    if (["buy", "sip", "top_up"].includes(t.type)) {
                        invValue -= amt;
                    } else if (["sell", "maturity"].includes(t.type)) {
                        invValue += amt;
                    }
                }
            });

            dayInvestments += invValue;
        });

        chartDataMap[dayKey] = {
            dateObj: d,
            savings: daySavings,
            liabilities: dayLiabilities,
            investments: dayInvestments
        };
    }

    const finalChartData = Object.values(chartDataMap).sort((a, b) => a.dateObj.getTime() - b.dateObj.getTime()).map(d => {
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const label = d.dateObj.getDate() === 1 ? `${monthNames[d.dateObj.getMonth()]} ${d.dateObj.getDate()}` : `${monthNames[d.dateObj.getMonth()]} ${d.dateObj.getDate()}`;
        
        return {
            name: label,
            date: d.dateObj.toISOString(),
            savings: Number(Math.max(0, d.savings).toFixed(2)),
            liabilities: -Number(Math.max(0, d.liabilities).toFixed(2)),
            investments: Number(Math.max(0, d.investments).toFixed(2)),
        };
    });


    // Fetch dynamic expense categories to display top 2 budgets
    const mainCategories = await db.category.findMany({
        where: { familyId: familyId, type: "expense", parentCategoryId: null },
        orderBy: { monthlyLimit: "desc" },
    });

    const categoryTotals: Record<string, number> = {};
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    transactions.forEach((t: any) => {
        // Exclude internal transfers (transferGroupId exists)
        if (t.transferGroupId) return;

        const tDate = new Date(t.date);
        if (t.displayType === 'expense' && tDate.getMonth() === currentMonth && tDate.getFullYear() === currentYear) {
            let parentId = t.categoryId;
            if (t.category && t.category.parentCategoryId) {
                parentId = t.category.parentCategoryId;
            }
            if (!categoryTotals[parentId]) categoryTotals[parentId] = 0;
            categoryTotals[parentId] += Number(t.amount);
        }
    });

    const topCategories = mainCategories.map(cat => ({
        id: cat.id,
        name: cat.name,
        spent: categoryTotals[cat.id] || 0,
        limit: Number(cat.monthlyLimit),
        colorClass: cat.colorClass,
        bgClass: cat.bgClass,
        icon: "CreditCard" // Using a generic icon for Dashboard short-view
    }));

    const budgetSummary = {
        totalSpent: Object.values(categoryTotals).reduce((a, b) => a + b, 0),
        totalLimit: mainCategories.reduce((acc, cat) => acc + Number(cat.monthlyLimit || 0), 0),
        overCount: mainCategories.filter(cat => (categoryTotals[cat.id] || 0) > Number(cat.monthlyLimit || 0)).length
    };

    // Calculate Dashboard Monthly Summary
    let monthlySalary = 0;
    let monthlyRent = 0;
    let monthlyOtherIncome = 0;
    let monthlyExpense = 0;
    let monthlyInvestment = 0;

    transactions.forEach((t: any) => {
        if (t.transferGroupId) return; // ignore internal transfers
        const tDate = new Date(t.date);
        if (tDate.getMonth() === currentMonth && tDate.getFullYear() === currentYear) {
            if (t.displayType === 'income') {
                const catName = t.category?.name?.toLowerCase() || '';
                if (catName.includes('salary')) {
                    monthlySalary += Number(t.amount);
                } else if (catName.includes('rent') || catName.includes('property')) {
                    monthlyRent += Number(t.amount);
                } else {
                    monthlyOtherIncome += Number(t.amount);
                }
            } else if (t.displayType === 'expense') {
                monthlyExpense += Number(t.amount);
            }
        }
    });

    portfolioInvestments.forEach((inv: any) => {
        (inv.transactions || []).forEach((t: any) => {
            const tDate = new Date(t.date);
            if (tDate.getMonth() === currentMonth && tDate.getFullYear() === currentYear) {
                if (["buy", "sip", "top_up"].includes(t.type)) {
                    monthlyInvestment += Number(t.amount);
                } else if (["sell", "maturity"].includes(t.type)) {
                    monthlyInvestment -= Number(t.amount);
                }
            }
        });
    });

    const totalIncome = monthlySalary + monthlyRent + monthlyOtherIncome;
    const monthlySavings = totalIncome - (monthlyExpense + Math.max(0, monthlyInvestment));

    const monthlySummary = {
        salary: monthlySalary,
        rent: monthlyRent,
        expense: monthlyExpense,
        investment: Math.max(0, monthlyInvestment),
        savings: monthlySavings,
        totalIncome
    };

    return {
        balance,
        savings,
        investments,
        liabilities,
        chartData: finalChartData,
        topCategories,
        budgetSummary,
        monthlySummary,
        currency: (family as any).currency,
        enabledModules: (family as any)?.enabledModules || []
    };
}

export async function getCurrentUser() {
    const { userId } = await getSession();
    if (!userId) return null;
    const user = await db.user.findUnique({
        where: { id: userId },
        select: { displayName: true, email: true, isBlocked: true }
    });
    
    const cookieStore = await cookies();
    const isAdminImpersonating = !!cookieStore.get("admin_token")?.value;

    return { ...user, isAdminImpersonating };
}

export async function getFamilyConfig() {
    const { familyId } = await getSession();
    if (!familyId) return null;
    return await db.family.findUnique({
        where: { id: familyId },
        select: { enabledModules: true, isBlocked: true, currency: true }
    });
}
